import { FC, useMemo } from 'react';
import {
  ActionMeta,
  InputActionMeta,
  PropsValue,
  SingleValue
} from 'react-select';
import AsyncSelect from 'react-select/async';
import Select, { ControlProps } from 'react-select';
import { SearchIcon } from '../Icons';
import classes from './styles.module.scss';
import { CrossIcon } from 'shared/icons';
import { TDropdownOption, TOptionProducts } from 'shared/types';

type SelectChangeProps = (
  newValue: SingleValue<TDropdownOption> | TOptionProducts,
  actionMeta?: ActionMeta<{
    value: string;
    label: string;
  }>
) => void | undefined;

export type SearchProps = {
  width?: string;
  height?: string;
  options?: TDropdownOption[];
  onChange?: SelectChangeProps;
  isClearable?: boolean;
  loadOptions?: any;
  value?: PropsValue<TDropdownOption>;
  onFocus?: () => void;
  asyncSelect?: boolean;
  className?: string;
  onInputChange?: (inputValue: string, actionMeta: InputActionMeta) => void;
  inputValue?: string;
  setInputValue?: (value: string) => void;
  getSelectRef?: (selectRef: React.RefObject<HTMLElement>) => void;
  placeholder?: string;
  isEmptyInputValue?: boolean;
  isSearchIcon?: boolean;
  isCrossIcon?: boolean;
  customStyles?: any;
  customOptionStyles?: any;
};

const Search: FC<SearchProps> = ({
  width,
  height,
  options,
  onChange,
  isClearable,
  loadOptions,
  value,
  onFocus,
  asyncSelect = false,
  className,
  onInputChange,
  inputValue,
  setInputValue,
  placeholder,
  isEmptyInputValue = false,
  isSearchIcon = true,
  isCrossIcon = true,
  customStyles,
  customOptionStyles = null
}) => {
  const getBackgroundColor = (state: any) => {
    const { isSelected, isFocused } = state;
    if (isSelected) return '#e6f7ff';
    if (isFocused) return '#f5f5f5';
    return 'transparent';
  };

  const defaultCustomStyles = useMemo(() => {
    return {
      control: (provided: ControlProps) => ({
        ...provided,
        border: '1px solid #E0E6EB',
        width: width,
        borderRadius: '12px',
        minHeight: '24px',
        height: height,
        background: '#fff',
        paddingLeft: '28px'
      }),
      valueContainer: (provided: ControlProps) => ({
        ...provided,
        padding: '0',
        height: '24px'
      }),
      singleValue: (provided: ControlProps) => ({
        ...provided,
        padding: '0',
        fontSize: '12px'
      }),
      indicatorsContainer: (provided: ControlProps) => ({
        ...provided,
        padding: '0',
        height: '24px'
      }),
      placeholder: (provided: ControlProps) => ({
        ...provided,
        fontSize: '12px',
        lineHeight: '16px',
        color: '#6A7383'
      }),
      input: (provided: ControlProps) => ({
        ...provided,
        fontSize: '12px',
        lineHeight: '16px',
        margin: '0',
        padding: '0',
        cursor: 'text'
      }),
      option: (provided: ControlProps, state: any) => ({
        ...provided,
        minHeight: '32px',
        padding: '5px 12px',
        fontSize: '14px',
        lineHeight: '22px',
        backgroundColor: getBackgroundColor(state),
        color: 'rgba(0, 0, 0, 0.85)',
        fontWeight: state.isSelected ? 'bold' : 'normal',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        transition: 'background 0.3s ease',
        '&:hover': {
          backgroundColor:
            state.isSelected && state.isFocused ? '#e6f7ff' : '#f5f5f5',
          color: 'rgba(0, 0, 0, 0.85)',
          cursor: 'pointer',
          transition: 'background 0.3s ease'
        },
        ...customOptionStyles
      }),
      menu: (provided: ControlProps) => ({
        ...provided,
        zIndex: 9
      }),
      menuList: (provided: ControlProps) => ({
        ...provided,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px'
        }
      })
    };
  }, [width, height, customOptionStyles]);

  const selectComponents = {
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
    LoadingIndicator: () => null
  };

  return (
    <>
      <div className={classes.search} style={{ height }}>
        {isSearchIcon && <SearchIcon className={classes.search__icon} />}
        {asyncSelect ? (
          <AsyncSelect
            isClearable={isClearable}
            cacheOptions
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              LoadingIndicator: () => null
            }}
            placeholder={placeholder || 'Search...'}
            styles={{ ...defaultCustomStyles, ...customStyles }}
            onChange={onChange}
            loadOptions={loadOptions}
            value={value}
            onFocus={onFocus}
            className={className}
            onInputChange={onInputChange}
          />
        ) : (
          <Select
            isClearable={isClearable}
            components={
              isEmptyInputValue
                ? { ...selectComponents, SingleValue: () => null }
                : selectComponents
            }
            placeholder={placeholder || 'Search...'}
            styles={{ ...defaultCustomStyles, ...customStyles }}
            options={options}
            value={value}
            onChange={onChange}
            inputValue={inputValue}
            onInputChange={onInputChange}
            className={className}
          />
        )}
        <div
          onClick={() =>
            setInputValue ? setInputValue('') : onChange && onChange(null)
          }
          className={classes.search__crossIcon}
        >
          {isCrossIcon &&
            (!!inputValue ||
              (!!value && (
                <CrossIcon width="10" height="10" color="#6A7383" />
              )))}
        </div>
      </div>
    </>
  );
};

export default Search;
